/**
 * extend your client code from this component and implement the methods
 */
import { ClientComponentLogger, LogErrorParams } from './client-component-logger';
import {
    trackAddToEvent,
    trackEvent,
    trackView,
    trackViewFeatures,
    trackAddActionToEvent,
    trackOnNextPageImpression,
    getGlobalEventMergeId,
} from './_common/client/tracking';
import { NamedTrackingFeatures, TrackingFeatures } from './tracking/client/event-data-types';
import { ActionName, DataContainer } from '@otto-ec/tracking-bct';

export abstract class BaseComponent {
    public abstract rootSelector: string;

    /**
     * Component logger. Comes from `ClientComponentRunner`
     */
    protected logger?: ClientComponentLogger;

    /**
     * initialize component with this method
     */
    public async init(): Promise<unknown> {
        return;
    }

    /**
     * will be called whenever ui-aggregator loads/reloads the HTML of this component, at the start or later.
     */
    public abstract onHtmlLoaded(): Promise<unknown>;

    /**
     * sets initialized logger, used by `ClientComponentRunner` to use same initialized logger object.
     */
    public async setLogger(logger: ClientComponentLogger) {
        this.logger = logger;
    }

    /**
     * use this method to track a submit event
     * @param data any data passed to click tracking
     */
    protected trackEvent(data: Record<string, unknown>): void {
        trackEvent(data);
    }

    protected trackOnNextPageImpression(data: DataContainer): void {
        trackOnNextPageImpression(data);
    }

    /**
     * use this method to track clicks and actions and to merge it to an existing submitted event.
     * @param data any data passed to click tracking
     * @param eventMergeId used for event tracking where an explicit event merge id is required. Otherwise the global event merge id will be used.
     */
    protected trackAddToEvent(data: Record<string, unknown>, actionName: ActionName, eventMergeId: string): void {
        trackAddToEvent(data, actionName, eventMergeId);
    }

    /**
     * use this method to track additional features on an event
     * @param data name of the action and features you want to track
     * @param eventMergeId eventMergeId of event you want to add the action to
     */
    protected trackAddActionToEvent(data: NamedTrackingFeatures, eventMergeId: string) {
        trackAddActionToEvent(data, eventMergeId);
    }

    /**
     * use this method to track a view
     * @param data any data passed to view tracking
     */
    protected trackView(data: Record<string, unknown>): void {
        trackView(data);
    }

    /**
     * use this method to track a view
     * @param data any data passed to view tracking
     */
    protected trackViewFeatures(data: TrackingFeatures): void {
        if (o_product.state.isVariationChanged) {
            const eventMergeId = getGlobalEventMergeId();
            if (eventMergeId === undefined) {
                throw new Error('No global event merge id is set after variation change, cannot merge action onto variation change event');
            }
            trackAddActionToEvent({ name: 'change', features: data }, eventMergeId);
        } else {
            trackViewFeatures(data);
        }
    }

    /**
     * similar to `querySelector()` but throws error if element not found
     * @param selectors query selectors
     * @param [parent=document] root element
     * @returns selected element if exists or throws error otherwise
     */
    protected notNullQuerySelector<T extends Element = HTMLElement>(selectors: string, parent: ParentNode = document): T {
        const element = parent.querySelector<T>(selectors);

        if (!element) {
            throw new Error(`can not select '${selectors}'`);
        }

        return element;
    }

    /**
     * @returns root element of component
     */
    protected getRootElement<T extends HTMLElement>(): T {
        return this.notNullQuerySelector(this.rootSelector);
    }

    /**
     * @return variation id of component root element
     * @protected
     */
    protected getVariationId(): string {
        return this.getRootElement().getAttribute('data-variation-id') || '';
    }

    /**
     * @param selectors query selectors
     * @returns child element of component
     */
    protected getChildElement<T extends HTMLElement>(selectors: string): T {
        return this.notNullQuerySelector(selectors, this.getRootElement());
    }

    /**
     * @param selectors query selectors
     * @returns array of child elements of component
     */
    protected getChildElements<T extends HTMLElement>(selectors: string): T[] {
        return Array.from(this.getRootElement().querySelectorAll(selectors));
    }

    /**
     * @returns `false` if component is not present in document, `true` otherwise
     */
    protected isPresent(): boolean {
        return !!document.querySelector(this.rootSelector);
    }

    /**
     * hides the component
     */
    protected hide() {
        this.getRootElement().classList.add('oc-visually-hidden');
    }

    /**
     * makes the component visible
     */
    protected show() {
        this.getRootElement().classList.remove('oc-visually-hidden');
    }

    /**
     * logs errors to server and in console
     * @param params error data to log
     * never throws any error
     */
    protected logError(params: LogErrorParams) {
        !this.logger ? console.error(params) : this.logger.logError(params);
    }
}
